import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import './FollowPreviewCard.css';

import AvatarPreview from '../../leftSidebar/pages/profile/avatar/AvatarPreview';
import { defaultAvatar } from '../../leftSidebar/pages/profile/avatar/avatarConfig';
import { unfollowUser } from '../../leftSidebar/pages/profile/social/followApi';

const apiUrl =
  process.env.REACT_APP_API_URL;

const FollowPreviewCard = ({
  onOpenAll,
  onOpenUser,
}) => {
  const [activeTab, setActiveTab] =
    useState('followers');

  const [following, setFollowing] =
    useState([]);

  const [followers, setFollowers] =
    useState([]);

  const [isLoading, setIsLoading] =
    useState(true);

  const [
    loadingFollowIds,
    setLoadingFollowIds,
  ] = useState([]);

  // ==========================================
  // INCARCARE FOLLOWING + FOLLOWERS
  // ==========================================

  const fetchFollowData =
    useCallback(
      async (
        showLoading = false
      ) => {
        if (showLoading) {
          setIsLoading(true);
        }

        try {
          const [
            followingResponse,
            followersResponse,
          ] = await Promise.all([
            fetch(
              `${apiUrl}/user/following`,
              {
                method: 'GET',
                credentials: 'include',
              }
            ),

            fetch(
              `${apiUrl}/user/followers`,
              {
                method: 'GET',
                credentials: 'include',
              }
            ),
          ]);

          if (
            !followingResponse.ok ||
            !followersResponse.ok
          ) {
            throw new Error(
              'Nu s-au putut incarca relatiile sociale.'
            );
          }

          const followingData =
            await followingResponse.json();

          const followersData =
            await followersResponse.json();

          setFollowing(
            Array.isArray(
              followingData
            )
              ? followingData
              : []
          );

          setFollowers(
            Array.isArray(
              followersData
            )
              ? followersData
              : []
          );

        } catch (error) {
          console.error(
            'Eroare FollowPreviewCard:',
            error
          );

        } finally {
          if (showLoading) {
            setIsLoading(false);
          }
        }
      },
      []
    );

  // ==========================================
  // INCARCARE INITIALA
  // ==========================================

  useEffect(() => {
    fetchFollowData(true);
  }, [fetchFollowData]);

  // ==========================================
  // ACTUALIZARE LIVE DUPA FOLLOW
  // ==========================================

  useEffect(() => {
    const handleFollowUpdated =
      () => {
        fetchFollowData(false);
      };

    window.addEventListener(
      'followUpdated',
      handleFollowUpdated
    );

    return () => {
      window.removeEventListener(
        'followUpdated',
        handleFollowUpdated
      );
    };
  }, [fetchFollowData]);

  // ==========================================
  // LISTA ACTIVA
  // ==========================================

  const users =
    activeTab === 'following'
      ? following
      : followers;

  const total =
    users.length;

  const remainingCount =
    Math.max(
      total - 3,
      0
    );

  // ==========================================
  // DESCHIDE LISTA COMPLETA
  // ==========================================

  const handleOpenAll = () => {
    if (onOpenAll) {
      onOpenAll(activeTab);
    }
  };

  // ==========================================
  // DESCHIDE PROFIL USER
  // ==========================================

  const handleOpenUser = (
    userId
  ) => {
    if (!userId) {
      return;
    }

    if (onOpenUser) {
      onOpenUser(userId);
    }
  };

  // ==========================================
  // URMARESTE UN URMARITOR
  // ==========================================

  const handleFollow = async (
    userId
  ) => {
    if (
      loadingFollowIds.includes(
        userId
      )
    ) {
      return;
    }

    setLoadingFollowIds(
      (current) => [
        ...current,
        userId,
      ]
    );

    try {
      const response = await fetch(
        `${apiUrl}/user/follow/${userId}`,
        {
          method: 'POST',
          credentials: 'include',

          headers: {
            'Content-Type':
              'application/json',
          },
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message ||
          'Nu s-a putut urmari utilizatorul.'
        );
      }

      window.dispatchEvent(
        new CustomEvent(
          'followUpdated',
          {
            detail: {
              action: 'follow',
              userId,
            },
          }
        )
      );

    } catch (error) {
      console.error(
        'Eroare follow:',
        error
      );

    } finally {
      setLoadingFollowIds(
        (current) =>
          current.filter(
            (id) =>
              id !== userId
          )
      );
    }
  };

  // ==========================================
  // NU MAI URMARI (tabul „Urmariti")
  // ==========================================
  // Lista se reincarca singura: unfollowUser emite `followUpdated`,
  // pe care il ascultam mai sus.

  const handleUnfollow = async (
    userId
  ) => {
    if (
      loadingFollowIds.includes(
        userId
      )
    ) {
      return;
    }

    setLoadingFollowIds(
      (current) => [
        ...current,
        userId,
      ]
    );

    try {
      await unfollowUser(userId);

    } catch (error) {
      console.error(
        'Eroare unfollow:',
        error
      );

    } finally {
      setLoadingFollowIds(
        (current) =>
          current.filter(
            (id) =>
              id !== userId
          )
      );
    }
  };

  // ==========================================
  // LOADING INITIAL
  // ==========================================

  if (isLoading) {
    return (
      <div className="follow-preview-card">

        <div className="follow-preview-loading">
          Se incarca...
        </div>

      </div>
    );
  }

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="follow-preview-card">

      {/* TABURI */}

      <div className="follow-preview-tabs">

        <button
          type="button"
          className={
            `follow-preview-tab ${
              activeTab === 'following'
                ? 'active'
                : ''
            }`
          }
          onClick={() =>
            setActiveTab(
              'following'
            )
          }
        >
          URMARITI
        </button>

        <button
          type="button"
          className={
            `follow-preview-tab ${
              activeTab === 'followers'
                ? 'active'
                : ''
            }`
          }
          onClick={() =>
            setActiveTab(
              'followers'
            )
          }
        >
          URMARITORI
        </button>

      </div>

      {/* USERS */}

      <div className="follow-preview-users">

        {users.length === 0 ? (

          <div className="follow-preview-empty">

            {activeTab ===
            'following'
              ? 'Nu urmaresti momentan nicio persoana.'
              : 'Nu ai momentan urmaritori.'}

          </div>

        ) : (

          users
            .slice(0, 3)
            .map((user) => {

              const userAvatar = {
                ...defaultAvatar,
                ...(
                  user.avatar_config ||
                  {}
                ),
              };

              const alreadyFollowing =
                following.some(
                  (followedUser) =>
                    followedUser.user_id ===
                    user.user_id
                );

              const isLoadingFollow =
                loadingFollowIds.includes(
                  user.user_id
                );

              return (
                <div
                  className="follow-preview-user"
                  key={user.user_id}
                >

                  {/* PROFIL CLICKABIL */}

                  <button
                    type="button"
                    className="follow-preview-profile"
                    onClick={() =>
                      handleOpenUser(
                        user.user_id
                      )
                    }
                    aria-label={
                      `Deschide profilul ${
                        user.display_name ||
                        'utilizatorului'
                      }`
                    }
                  >

                    <div className="follow-preview-avatar-wrapper">

                      <AvatarPreview
                        avatar={
                          userAvatar
                        }
                        className="avatar-preview-follow-card"
                      />

                    </div>

                    <div className="follow-preview-info">

                      <div className="follow-preview-name">

                        {user.display_name ||
                          'Utilizator'}

                      </div>

                      <div className="follow-preview-xp">

                        {Number(
                          user.xp || 0
                        ).toLocaleString(
                          'ro-RO'
                        )}{' '}
                        XP

                      </div>

                    </div>

                  </button>

                  {/* FOLLOW BACK */}

                  {activeTab ===
                    'followers' &&
                    !alreadyFollowing && (

                      <button
                        type="button"
                        className="follow-preview-follow"
                        disabled={
                          isLoadingFollow
                        }
                        onClick={() =>
                          handleFollow(
                            user.user_id
                          )
                        }
                        aria-label={
                          `Urmareste ${
                            user.display_name ||
                            'utilizator'
                          }`
                        }
                      >

                        {isLoadingFollow
                          ? '...'
                          : '+'}

                      </button>

                    )}

                  {/* NU MAI URMARI */}

                  {activeTab ===
                    'following' && (

                      <button
                        type="button"
                        className="follow-preview-unfollow"
                        disabled={
                          isLoadingFollow
                        }
                        onClick={() =>
                          handleUnfollow(
                            user.user_id
                          )
                        }
                        aria-label={
                          `Nu mai urmari ${
                            user.display_name ||
                            'utilizatorul'
                          }`
                        }
                        title="Nu mai urmari"
                      >

                        {isLoadingFollow
                          ? '...'
                          : '−'}

                      </button>

                    )}

                </div>
              );
            })

        )}

      </div>

      {/* VEZI RESTUL */}

      {remainingCount > 0 && (

        <button
          type="button"
          className="follow-preview-more"
          onClick={
            handleOpenAll
          }
        >

          <span>
            Vezi inca{' '}
            {remainingCount}
          </span>

          <span className="follow-preview-arrow">
            ›
          </span>

        </button>

      )}

    </div>
  );
};

export default FollowPreviewCard;